import { lazy, type ComponentType } from 'react';
import { matchPath } from 'react-router-dom';

/**
 * Route-code prefetch on intent (2026-09-24).
 *
 * Every page except the homepage is a lazy chunk that used to start
 * downloading only after the click. Now it starts when the visitor shows
 * intent — pointer over a link, a touch, or keyboard focus — so the click
 * usually finds the code already there. The free-diagnosis page (the site's
 * main conversion action) is also warmed once the browser is idle.
 *
 * Only the public routes passed to installRoutePrefetch() are prefetched;
 * admin and auth pages never are. Nothing runs under Save-Data.
 */

type Importer = () => Promise<unknown>;

/** React.lazy() that also exposes its import, so the same chunk can be warmed early. */
export const lazyPage = <T extends ComponentType<object>>(factory: () => Promise<{ default: T }>) =>
  Object.assign(lazy(factory), { preload: factory as Importer });

let routes: Array<[string, Importer]> = [];
const started = new Set<Importer>();
let inFlight = 0;

/**
 * True while a prefetch is loading. main.tsx uses it so that a failed
 * prefetch in a tab left open across a deploy doesn't reload the page under
 * the visitor's cursor; the click itself still triggers that reload.
 */
export const isRoutePrefetching = () => inFlight > 0;

const findImporter = (pathname: string) =>
  routes.find(([pattern]) => matchPath({ path: pattern, end: true }, pathname))?.[1];

export function prefetchRoute(pathname: string) {
  const importer = findImporter(pathname);
  if (!importer || started.has(importer)) return;
  started.add(importer);
  inFlight++;
  importer()
    .catch(() => { /* the click retries (and handles a stale build) */ })
    .finally(() => { inFlight--; });
}

/**
 * Load a route's code now and wait for it — the static-page handoff
 * (src/lib/staticHandoff.ts) does this before rendering. Resolves once loaded,
 * or at once for a route that isn't in the table; never rejects.
 */
export function preloadRoute(pathname: string): Promise<void> {
  const importer = findImporter(pathname);
  if (!importer) return Promise.resolve();
  started.add(importer);
  return importer().then(() => undefined, () => undefined);
}

export function installRoutePrefetch(table: Array<[string, Importer]>) {
  routes = table;
  if (typeof window === 'undefined') return;
  const connection = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection;
  if (connection?.saveData) return;

  const onIntent = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement)) return;
    if (link.target === '_blank' || link.origin !== window.location.origin) return;
    if (link.pathname === window.location.pathname) return;
    prefetchRoute(link.pathname);
  };
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);

  const warmDiagnose = () => prefetchRoute('/diagnose');
  const whenIdle = () => {
    if ('requestIdleCallback' in window) window.requestIdleCallback(warmDiagnose, { timeout: 5000 });
    else setTimeout(warmDiagnose, 3000);
  };
  if (document.readyState === 'complete') whenIdle();
  else window.addEventListener('load', whenIdle, { once: true });
}
